// Prefer Vite env var when available (Vite uses import.meta.env.VITE_API_URL).
// Fall back to CRA-style REACT_APP_API_BASE_URL for other setups.
const envBase = (typeof import.meta !== 'undefined' && (import.meta as any).env && (import.meta as any).env.VITE_API_URL)
    ? (import.meta as any).env.VITE_API_URL
    : (process.env.REACT_APP_API_BASE_URL || '');
const baseUrl = (envBase || '').replace(/\/+$/, '');

// Exported base URL for other components to build absolute URLs to the API (images, static files)
export const ApiBaseUrl = baseUrl;

/**
 * Utility: build query string from plain object (string values only).
 */
export function buildQueryString(params?: Record<string, string | undefined | null>) {
    if (!params) return '';
    const parts: string[] = [];
    Object.keys(params).forEach(k => {
        const v = params[k];
        if (v !== undefined && v !== null && String(v).length > 0) {
            parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
        }
    });
    return parts.length > 0 ? `?${parts.join('&')}` : '';
}

/**
 * Convenience GET that accepts a params object and appends to the url.
 */
export async function getWithParams(url: string, params?: Record<string, string | undefined | null>) {
    const q = buildQueryString(params);
    return request('GET', url + q);
}

/**
 * Build an absolute URL for an image stored on the API server.
 * If fileName is omitted, returns the provided fallback (relative to API base) or a default image.
 */
export function getImageUrl(fileName?: string, fallback?: string): string | null {
    // Some deployments set VITE_API_URL to the API prefix (e.g. "http://host:5000/api/").
    // Static files are served from the web root (e.g. "/images/..."), so remove any trailing
    // "/api" segment from the base when building image URLs.
    const staticBase = baseUrl.replace(/\/api\/?$/i, '');

    // If an actual file name is provided, return the full URL to that image on the server static files
    if (fileName && fileName.length > 0) {
        // When running in the browser prefer a same-origin relative path so images are
        // requested via the current site's scheme/host (this avoids mixed-content issues
        // when the page is HTTPS but the configured API base is HTTP/IP).
        try {
            if (typeof window !== 'undefined') {
                const rel = `/images/${fileName}`;
                try { console.debug('[ApiClient] getImageUrl fileName (relative):', fileName, '->', rel); } catch {};
                return rel;
            }
        } catch { }

        const url = `${staticBase}/images/${fileName}`;
        try { console.debug('[ApiClient] getImageUrl fileName:', fileName, '->', url); } catch {};
        return url;
    }

    // If a fallback is provided and looks like a path/URL or ends with a common image extension,
    // treat it as a relative path on the API server and return a full URL.
    if (fallback && fallback.length > 0) {
        const lower = fallback.toLowerCase();
        const looksLikePath = /(^https?:\/\/)|(^\/)|\/.+|\.(jpg|jpeg|png|gif|svg)$/.test(lower);
        if (looksLikePath) {
            // If fallback is already an absolute URL and we're in the browser, prefer a
            // same-origin relative path to avoid mixed-content and cross-origin image loads.
            try {
                if (typeof window !== 'undefined') {
                    try {
                        // If fallback is absolute, extract the pathname; if it's already relative
                        // (starts with '/'), use as-is.
                        if (/^https?:\/\//i.test(fallback)) {
                            const u = new URL(fallback);
                            const rel = u.pathname + (u.search || '');
                            try { console.debug('[ApiClient] getImageUrl fallback(absolute->relative):', fallback, '->', rel); } catch {};
                            return rel;
                        }
                        const rel = `/${fallback.replace(/^\/+/, '')}`;
                        try { console.debug('[ApiClient] getImageUrl fallback(path):', fallback, '->', rel); } catch {};
                        return rel;
                    } catch {
                        // fallback to constructing from staticBase when URL parsing fails
                    }
                }
            } catch { }

            const url = `${staticBase}/${fallback.replace(/^\/+/, '')}`;
            try { console.debug('[ApiClient] getImageUrl fallback(path):', fallback, '->', url); } catch {};
            return url;
        }

        // If fallback appears to be plain text (a label), return a small inline SVG data URL
        // containing that text so callers can set it as `img.src` without any network fetch.
        try {
            const escapeXml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
            const text = escapeXml(fallback);
            const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='400' height='240'><rect fill='%23efefef' width='100%' height='100%'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='%23666' font-family='Arial, Helvetica, sans-serif' font-size='20'>${text}</text></svg>`;
            const dataUrl = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
            try { console.debug('[ApiClient] getImageUrl fallback(text) -> data url'); } catch {};
            return dataUrl;
        } catch {
            return null;
        }
    }

    // No file and no fallback: return null so callers can render a textual placeholder instead
    try { console.debug('[ApiClient] getImageUrl -> no image available'); } catch {};
    return null;
}

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
// tokenProvider: default reads localStorage but tests or app can override
export let tokenProvider: () => string | null = () => {
    try { return typeof window !== 'undefined' ? localStorage.getItem('token') : null; } catch { return null; }
};

export function setTokenProvider(fn: () => string | null) {
    tokenProvider = fn;
}

async function request(method: HttpMethod, url: string, body?: any): Promise<any> {
    // Build full URL for API or static requests.
    // If caller passed an absolute URL (http/https) use it directly.
    // Otherwise, ensure API endpoints are requested under `/api` while leaving static paths
    // (like `/images/...`) untouched.
    let requestPath = url;
    const isAbsolute = requestPath.startsWith('http://') || requestPath.startsWith('https://');
    if (!isAbsolute) {
        if (!requestPath.startsWith('/')) requestPath = '/' + requestPath;
        const isApiOrStatic = requestPath.startsWith('/api') || requestPath.startsWith('/images') || requestPath.startsWith('/static') || requestPath.startsWith('/assets');
        if (!isApiOrStatic) {
            // treat as API endpoint by default
            requestPath = '/api' + requestPath;
        }
    }
    // Prefer relative API paths when running in the browser so requests automatically
    // use the page's scheme (http or https) and avoid mixed-content issues.
    let fullUrl: string;
    if (isAbsolute) {
        fullUrl = requestPath;
    } else {
        try {
            if (typeof window !== 'undefined') {
                // Running in browser: use relative path so the browser will request
                // the API using the same origin/protocol (e.g. https).
                fullUrl = requestPath;
            } else {
                // Not running in a browser (SSR/tests): fall back to configured baseUrl
                fullUrl = `${baseUrl}${requestPath}`;
            }
        } catch {
            fullUrl = `${baseUrl}${requestPath}`;
        }
    }
    const headers: Record<string, string> = {};

    // Resolve token via provider (default reads localStorage). Tests can override provider.
    try {
        const tok = tokenProvider();
        if (tok) {
            headers['Authorization'] = `Bearer ${tok}`;
            // dev-only logging of token being sent (masked)
            try {
                const isDev = typeof import.meta !== 'undefined' && (import.meta as any).env && (import.meta as any).env.DEV;
                if (isDev) {
                    const masked = tok.length > 8 ? tok.substring(0, 4) + '...' + tok.substring(tok.length - 4) : tok;
                    // eslint-disable-next-line no-console
                    console.debug('[ApiClient] Using token', masked);
                }
            } catch { }
        }
    } catch { /* ignore when provider fails */ }

    const init: RequestInit = {
        method,
        headers,
        credentials: 'same-origin', // adjust if your API requires 'include'
    };

    // If body is FormData (file upload), don't set Content-Type and use body directly
    if (body !== undefined) {
        if (typeof FormData !== 'undefined' && body instanceof FormData) {
            // let fetch set Content-Type with boundary
            init.body = body;
        } else {
            headers['Content-Type'] = 'application/json';
            init.body = JSON.stringify(body);
        }
    }

    const res = await fetch(fullUrl, init);
    const text = await res.text();
    let data: any = null;
    try {
        data = text ? JSON.parse(text) : null;
    } catch {
        // If response is not JSON, keep raw text
        data = text;
    }

    if (!res.ok) {
        const err: any = new Error(res.statusText || 'Request failed');
        err.status = res.status;
        err.data = data;
        throw err;
    }

    return data;
}

const ApiClient = {
    get: (url: string) => request('GET', url),
    getWithParams,
    post: (url: string, body?: any) => request('POST', url, body),
    put: (url: string, body?: any) => request('PUT', url, body),
    patch: (url: string, body?: any) => request('PATCH', url, body),
    delete: (url: string) => request('DELETE', url),
    // add other helpers if needed
};
export default ApiClient;